import { getAppInfo, getLGUserInfoV2, getOSInfo, getWebFUserInfo } from './jsb';
import NioCloudAppJsBridge from './jsb/nio_cloud_app_jsbridge';

let runtime: 'unknown' | 'lg' | 'wx' | 'cloud_app' | 'webf' = 'unknown';

export function initRuntime() {
    try {
        if (currentInLgContainer()) {
            runtime = 'lg';
        } else if (currentInMPContainer()) {
            runtime = 'wx';
        } else if (currentInCloudAppContainer()) {
            runtime = 'cloud_app';
        } else if (currentInWebFContainer()) {
            runtime = 'webf';
        }
    } catch (err) {
        console.error(err);
    }
}

export function getRuntime() {
    return runtime;
}

export function getNioAppVersion() {
    if (runtime === 'lg' || runtime === 'webf') {
        try {
            const result = getAppInfo();
            return result?.data?.version ?? 'NA';
        } catch (err) {
            console.log(err);
            return 'NA';
        }
    }

    return 'NA';
}

// console.log(`[NIO APM Plus] client NIO_APM_PLUS runtime: ${runtime}`);

/**
 * 当前是否在 webf 容器中
 */
export function currentInWebFContainer() {
    const lowCaseUA = navigator.userAgent.toLowerCase();
    return lowCaseUA.includes('webf/');
}

/**
 * 当前是否在车机 App 中
 * @todo 改成 ua 判断.(目前 ua 没有特征值)
 */
export function currentInCloudAppContainer() {
    const lowCaseUA = navigator.userAgent.toLowerCase();
    // todo: [answer] url 判断逻辑迭代几个版本后, 移除
    return lowCaseUA.includes('wv=cloudapp') || location.href.includes('is_cloud_app=true');
}

/**
 * current in wechat miniprogram webview
 *
 * 从微信7.0.0开始，可以通过判断userAgent中包含miniProgram字样来判断小程序 web-view 环境。
 */
export function currentInMPContainer() {
    return navigator.userAgent.includes('miniProgram');
}

/**
 * current in lg container
 */
export function currentInLgContainer() {
    // judge by user agent (suitable for all nio app(ios, android, cn, eu))
    const lowCaseUA = navigator.userAgent.toLowerCase();
    return lowCaseUA.includes('webview/lg');
}

/**
 * current in nio cn android app
 *
 * Mozilla/5.0 (Linux; Android 13; sdk_gphone64_arm64 Build/TE1A.220922.012; wv)
 * AppleWebKit/537.36 (KHTML, like Gecko) Version/4.0
 * Chrome/103.0.5060.71 Mobile Safari/537.36
 * NioAppCN/5.13.0(OS:Android) webview/lg _dsbridge AD_CUSTAPPCCIC
 */
export function currentInNioCNAndroid() {
    const lowCaseUA = navigator.userAgent.toLowerCase();
    return lowCaseUA.includes('android') && lowCaseUA.includes('nioappcn') && lowCaseUA.includes('webview/lg');
}

export function getUserID(): string | undefined {
    if (runtime === 'wx') {
        const url = new URL(window.location.href);
        return url.searchParams.get('distinctId') ?? void 0;
    }
    try {
        if (runtime === 'lg') {
            const result = getLGUserInfoV2();
            return result.data?.account_id ?? result.data?.accountId;
        } else if (runtime === 'webf') {
            const result = getWebFUserInfo();
            return result.data?.account_id ?? result.data?.accountId;
        } else if (runtime === 'cloud_app') {
            const result = NioCloudAppJsBridge.getLoginInfo();
            return result.data?.profile_id;
        }
    } catch (err) {
        console.log(err);
    }
    return void 0;
}

export function getEnv(): string {
    if (runtime === 'lg' || runtime === 'webf') {
        try {
            const result = getAppInfo();
            return result.data?.env ?? getEnvByHost();
        } catch (err) {
            console.log(err);
        }
    } else if (
        // @ts-ignore
        typeof window?.FX_DEPLOY_ENV === 'string' &&
        // @ts-ignore
        window.FX_DEPLOY_ENV?.length > 0
    ) {
        // @ts-ignore
        return window.FX_DEPLOY_ENV;
    }
    return getEnvByHost();
}

export function getEnvByHost(): string {
    try {
        const host = window.location.hostname ?? window.location.host;
        if (host === '127.0.0.1' || host === 'localhost' || host.indexOf('-local') > -1 || validateIpAddress(host)) {
            return 'local';
        }
        if (host.indexOf('-dev') > -1 || host.indexOf('-qa') > -1) {
            return 'dev';
        }
        if (host.indexOf('-test') > -1 || host.indexOf('-sit') > -1) {
            return 'test';
        }
        if (host.indexOf('-stg') > -1 || host.indexOf('-uat') > -1) {
            return 'stg';
        }
    } catch (err) {
        console.log(err);
    }
    return 'prod';
}

export function validateIpAddress(ipaddress: string) {
    return /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/.test(
        ipaddress
    );
}

export function getDeviceID(): string | undefined {
    if (runtime === 'lg' || runtime === 'webf') {
        try {
            const result = getOSInfo();
            return result?.data?.device_id;
        } catch (err) {
            console.log(err);
        }
    }
    return void 0;
}

export function getMobileInfo():
    | {
          brand: string;
          model: string;
          systemName: string;
          systemVersion: string;
      }
    | undefined {
    if (runtime === 'lg' || runtime === 'webf') {
        try {
            const result = getOSInfo();
            return {
                brand: result?.data?.brand,
                model: result.data?.model,
                systemName: result.data?.systemName,
                systemVersion: result.data?.systemVersion,
            };
        } catch (err) {
            console.log(err);
            return void 0;
        }
    }
    return void 0;
}

/**
 *
 * @param value
 * @return {boolean}
 * @see https://github.com/lodash/lodash/blob/master/isObject.js#LL24-L27C2
 */
export function isObject(value: any) {
    const type = typeof value;
    return value != null && (type === 'object' || type === 'function');
}

/**
 * Gets the `toStringTag` of `value`.
 *
 * @private
 * @param {*} value The value to query.
 * @returns {string} Returns the `toStringTag`.
 */
export function getTag(value: any) {
    if (value == null) {
        return value === undefined ? '[object Undefined]' : '[object Null]';
    }
    return toString.call(value);
}

/**
 *
 * @param value
 * @return {boolean}
 * @see https://github.com/lodash/lodash/blob/master/isString.js#L18
 */
export function isString(value: string) {
    const type = typeof value;
    return (
        type === 'string' ||
        (type === 'object' && value != null && !Array.isArray(value) && getTag(value) === '[object String]')
    );
}

/**
 *
 * @param value
 * @return {boolean}
 * @see https://github.com/lodash/lodash/blob/master/isFunction.js#LL28-L30C2
 */
export function isFunction(value: any) {
    return typeof value === 'function';
}

/**
 @description 类型参数检测器
 @param {object|string} context - 入参
 @param {object} [context.data] - 业务参数
 @param {string} [context.event] - 业务参数
 @param {object} [context.callback] - 业务参数

 @return {void}
 */
export function checker(context: any) {
    if (context !== undefined) {
        if (!isObject(context)) {
            throw new Error(
                'error info:  error param type ， need object has context.data or context.callback or context.event'
            );
        }
        if (context.data !== undefined) {
            if (!isObject(context.data)) {
                throw new Error('error info:  context.data type need Object');
            }
        }
        if (context.event !== undefined) {
            if (!isString(context.event)) {
                throw new Error('error info:  context.event type need String');
            }
        }
        if (context.callback !== undefined) {
            if (!isFunction(context.callback)) {
                throw new Error('error info:  context.callback type need Function');
            }
        } else {
            context.callback = () => null;
        }
    }
}

const KEY = 'NIO_APM_TYPE';
export const ELASTIC_APM_VALUE = 'elastic_apm';

/**
 * 返回localStorage中缓存的APM类型
 * @returns {string | undefined}
 */
export function getApmType() {
    try {
        const apmType = localStorage.getItem(KEY);
        if (apmType)
            return apmType;
    } catch (err) {
    }
    return undefined;
}

/**
 * localStorage中缓存APM类型
 * @param apmType {string | undefined}
 */
export function setApmType(apmType: string | undefined) {
    try {
        if (apmType === undefined) {
            localStorage.removeItem(KEY);
            return;
        }
        localStorage.setItem(KEY, apmType);
    } catch (err) {
    }
}

/**
 * 获取北极星配置
 * @param config {{ group: string, key: string}}
 * @param env {string} "dev" | "test" | "stg" | "prod"
 * @returns 
 */
export async function fetchNorthPolarisConfig(config: any, env: string) {
    let serviceUrl = ''
    if (env === 'dev') {
        serviceUrl = `https://gateway-front-external-dev.nioint.com/moat/10086`;
    } else if (env === 'test') {
        serviceUrl = `https://gateway-front-external-test.nioint.com/moat/10086`;
    } else if (env === 'stg') {
        serviceUrl = `https://gateway-front-external-stg.nio.com/moat/10086`;
    } else {
        serviceUrl = `https://gateway-front-external.nio.com/moat/10086`;
    }
    const codeString = `${config.group}.platform.common.keyname.${config.key}`
    try {
        const response = await fetch(
            `${serviceUrl}/n/c/csd-config/config/group/batch`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    code_list: [codeString],
                    biz_code: 'nio-h5-cn'
                })
            }
        );
        const data = await response.json();

        // 在这里处理解析后的实际数据
        const northPolarisConfigJSON = data?.data?.[codeString]?.[codeString]
        if (northPolarisConfigJSON) {
            console.log('北极星配置数据', JSON.parse(northPolarisConfigJSON))
            const config = JSON.parse(northPolarisConfigJSON);
            return config;
        }
    } catch (error) {
        console.error('Error fetching North Polaris config:', error);
    }
    return undefined;
}

const APM_TYPE_KEY_CONFIG = {
    group: 'apm-sample-rate',
    key: 'apm-default'
};

/**
 * 从远端（北极星）同步开关配置，并保存至本地
 * @param env {string} "dev" | "test" | "stg" | "prod"
 */
export async function syncLocalConfigFromRemote(env: string) {
    try {
        const remoteConfig = await fetchNorthPolarisConfig(APM_TYPE_KEY_CONFIG, env);
        if (remoteConfig !== undefined) {
            const rate = parseFloat(remoteConfig.useElasticApm);
            if (Math.random() < rate) {
                setApmType(ELASTIC_APM_VALUE);
            } else {
                setApmType('default_apm');
            }
        }
    } catch (err) {
    }
}

export const CURRENT_SDK_VERSION = '2.12.3';
